import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useCart } from '../../context/CartContext';
import supabase from '../../supabase/supabase';

const {
  FiX, FiCheckCircle, FiLock, FiAlertCircle, FiHash, FiCreditCard,
  FiChevronRight, FiExternalLink, FiRotateCcw, FiClock, FiUser, FiMapPin, FiArrowRight, FiPhone
} = FiIcons;

const PAYMENT_PROVIDERS = [
  { 
    id: 'payfast', 
    name: 'PayFast', 
    description: 'Pay securely with card, EFT and more.', 
    color: '#0077c8', 
    bg: 'bg-blue-50', 
    border: 'border-blue-300', 
    activeBg: 'bg-blue-600', 
    icon: '💳',
    logoUrl: 'https://media-manager-c.questera.ai/greta-media/905f91485ab63acb0ca3d0eb7a8d12edb0e5fd8263e0c7ad4c0bd7710abc0b3f680db9e50b9a6deeaa0ab59826a97d73/images/aW1hZ2UvaGVpYw==/453cbc47ca9eeb25ae71e4daf3d6556c.heic'
  },
];

const PAYFAST_SANDBOX_URL = 'https://sandbox.payfast.co.za/eng/process';
const PAYFAST_LIVE_URL = 'https://www.payfast.co.za/eng/process';
const SANDBOX_FALLBACK_MERCHANT_ID = '10000100';
const SANDBOX_FALLBACK_MERCHANT_KEY = '46f0cd694581a';

const SESSION_CART_KEY = 'pf_cart_backup';
const SESSION_CUSTOMER_KEY = 'pf_customer_backup';
const SESSION_PROJECT_KEY = 'pf_project_backup';
const SESSION_TOTAL_KEY = 'pf_total_backup';
const SESSION_PROVIDER_KEY = 'pf_provider_backup';

function savePrePaymentSession({ cartItems, customer, projectId, total, provider }) {
  try {
    sessionStorage.setItem(SESSION_CART_KEY, JSON.stringify(cartItems));
    sessionStorage.setItem(SESSION_CUSTOMER_KEY, JSON.stringify(customer));
    sessionStorage.setItem(SESSION_PROJECT_KEY, projectId || '');
    sessionStorage.setItem(SESSION_TOTAL_KEY, String(total));
    sessionStorage.setItem(SESSION_PROVIDER_KEY, provider || '');
  } catch (_) {}
}

function loadPrePaymentSession() {
  try {
    const cart = JSON.parse(sessionStorage.getItem(SESSION_CART_KEY) || 'null');
    const customer = JSON.parse(sessionStorage.getItem(SESSION_CUSTOMER_KEY) || 'null');
    const projectId = sessionStorage.getItem(SESSION_PROJECT_KEY) || null;
    const total = parseFloat(sessionStorage.getItem(SESSION_TOTAL_KEY) || '0');
    const provider = sessionStorage.getItem(SESSION_PROVIDER_KEY) || '';
    return { cart, customer, projectId, total, provider };
  } catch (_) { return null; }
}

function clearPrePaymentSession() {
  try {
    [SESSION_CART_KEY, SESSION_CUSTOMER_KEY, SESSION_PROJECT_KEY, SESSION_TOTAL_KEY, SESSION_PROVIDER_KEY]
      .forEach(k => sessionStorage.removeItem(k));
  } catch (_) {}
}

async function buildPayFastUrl({ projectId, tempRef, customerName, customerEmail, cartTotal }) {
  try {
    let query = supabase.from('store_settings_1773100000000').select('value').eq('key', 'payment_payfast');
    if (projectId) query = query.eq('project_id', projectId);
    const { data } = await query.single();
    const settings = data?.value || {};
    const credentials = settings.credentials || {};
    const merchantId = credentials.merchant_id?.trim() || '';
    const merchantKey = credentials.merchant_key?.trim() || '';
    const passphrase = credentials.passphrase?.trim() || '';
    const hasRealCredentials = !!merchantId && !!merchantKey;
    const sandboxMode = settings.sandbox_mode !== false || !hasRealCredentials;
    const finalMerchantId = hasRealCredentials ? merchantId : SANDBOX_FALLBACK_MERCHANT_ID;
    const finalMerchantKey = hasRealCredentials ? merchantKey : SANDBOX_FALLBACK_MERCHANT_KEY;
    const baseUrl = sandboxMode ? PAYFAST_SANDBOX_URL : PAYFAST_LIVE_URL;
    const origin = window.location.origin;
    const params = new URLSearchParams({
      merchant_id: finalMerchantId, merchant_key: finalMerchantKey,
      return_url: `${origin}/?payfast_return=success&ref=${tempRef}`,
      cancel_url: `${origin}/?payfast_return=cancel&ref=${tempRef}`,
      notify_url: `${origin}/api/payfast-notify`,
      name_first: customerName.split(' ')[0] || customerName,
      name_last: customerName.split(' ').slice(1).join(' ') || '',
      email_address: customerEmail, m_payment_id: tempRef,
      amount: Number(cartTotal).toFixed(2), item_name: `Store Order`,
      item_description: `Payment reference: ${tempRef}`,
    });
    if (passphrase) params.append('passphrase', passphrase);
    return { url: `${baseUrl}?${params.toString()}`, isSandbox: sandboxMode, hasRealCredentials };
  } catch (err) {
    console.error('Failed to build PayFast URL:', err);
    return null;
  }
}

const PaymentMethodCard = ({ provider, isSelected, onSelect, s }) => {
  const accent = s.checkoutAccent || '#5494d4';
  return (
    <button
      type="button"
      onClick={() => onSelect(provider.id)}
      className={`w-full flex items-center space-x-4 p-4 rounded-xl border-2 transition-all text-left ${
        isSelected ? `${provider.border} ${provider.bg} shadow-sm` : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
      }`}
    >
      <div className={`w-14 h-10 rounded-lg flex items-center justify-center shrink-0 ${provider.logoUrl ? 'bg-white p-1 border border-gray-100' : (isSelected ? provider.activeBg : 'bg-gray-100')} overflow-hidden`}>
        {provider.logoUrl ? (
          <img src={provider.logoUrl} alt={provider.name} className="w-full h-full object-contain" />
        ) : (
          <span className="text-xl">{provider.icon}</span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className={`font-bold text-base ${isSelected ? 'text-gray-900' : 'text-gray-700'}`}>{provider.name}</p>
        <p className="text-xs text-gray-500 truncate mt-0.5">{provider.description}</p>
      </div>
      <div className={`w-5 h-5 rounded-full border-2 shrink-0 ${isSelected ? '' : 'border-gray-300'}`}
        style={isSelected ? { borderColor: provider.color, backgroundColor: provider.color } : {}}>
        {isSelected && <div className="w-full h-full flex items-center justify-center"><div className="w-2 h-2 bg-white rounded-full" /></div>}
      </div>
    </button>
  );
};

const CheckoutModalInner = () => {
  const { setIsCheckoutModalOpen, cartItems, cartTotal, clearCart, addToCart, projectId, storeStyles: s } = useCart();

  const accent = s.checkoutAccent || '#5494d4';
  const accentText = s.checkoutAccentText || '#ffffff';
  const checkoutBg = s.checkoutBg || '#ffffff';
  const headerBg = s.checkoutHeaderBg || '#f9fafb';
  const inputBg = s.checkoutInputBg || '#ffffff';
  const inputBorder = s.checkoutInputBorder || '#e5e7eb';
  const titleColor = s.checkoutTitleColor || '#111827';
  const textColor = s.checkoutTextColor || '#6b7280';
  const btnRadius = s.checkoutButtonRadius || '0.5rem';

  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ 
    name: '', email: '', phone: '', password: '', repeatPassword: '',
    address: '', city: '', zipCode: '', country: 'South Africa', state: '', company: ''
  });
  
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('payfast');
  const [enabledProviders, setEnabledProviders] = useState([]);
  const [loadingProviders, setLoadingProviders] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderResult, setOrderResult] = useState(null);
  const [error, setError] = useState('');

  const taxAmount = cartTotal * 0.15; // 15% VAT for display

  useEffect(() => {
    const url = new URL(window.location.href);
    const payfastReturn = url.searchParams.get('payfast_return');
    if (payfastReturn === 'success') {
      const createOrderAfterPayment = async () => {
        const session = loadPrePaymentSession();
        if (!session?.cart || !session?.customer) { window.history.replaceState({}, document.title, window.location.pathname); return; }
        
        try {
          const total = session.total || session.cart.reduce((s, i) => s + Number(i.price) * i.quantity, 0);
          
          // 1. Insert Customer
          const { data: customerData } = await supabase.from('store_customers_1776316637000').insert({
            project_id: session.projectId,
            full_name: session.customer.name,
            email: session.customer.email,
            phone: session.customer.phone,
            address: session.customer.address,
            city: session.customer.city,
            zip_code: session.customer.zipCode,
            country: session.customer.country,
            state: session.customer.state,
            company_name: session.customer.company
          }).select('id').single();

          // 2. Insert Order
          const { data: order, error: orderError } = await supabase.from('store_orders_1772955957776').insert({ 
            customer_id: customerData?.id,
            customer_name: session.customer.name, 
            customer_email: session.customer.email,
            customer_phone: session.customer.phone,
            billing_address: session.customer.address,
            billing_city: session.customer.city,
            billing_zip: session.customer.zipCode,
            billing_country: session.customer.country,
            billing_state: session.customer.state,
            billing_company: session.customer.company,
            total, 
            status: 'paid', 
            payment_method: session.provider || 'payfast', 
            ...(session.projectId ? { project_id: session.projectId } : {}) 
          }).select('id, order_number').single();
          
          if (orderError) throw orderError;
          
          // 3. Insert Items
          await supabase.from('store_order_items_1772955957776').insert(
            session.cart.map(item => ({ order_id: order.id, product_id: item.id, quantity: item.quantity, price: item.price }))
          );
          
          clearPrePaymentSession();
          clearCart();
          setOrderResult({ orderNumber: order.order_number, paymentMethod: session.provider || 'payfast' });
        } catch (err) {
          console.error('Failed to create order after payment:', err);
          setError('Payment was received but order logging failed. Please contact support.');
        } finally {
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      };
      createOrderAfterPayment();
    } else if (payfastReturn === 'cancel') {
      const session = loadPrePaymentSession();
      if (session?.cart && session.cart.length > 0 && cartItems.length === 0) session.cart.forEach(item => { for (let i = 0; i < item.quantity; i++) addToCart(item); });
      clearPrePaymentSession();
      setError('Payment was cancelled. Your cart has been restored.');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  useEffect(() => {
    const fetchProviders = async () => {
      setLoadingProviders(true);
      try {
        const keys = PAYMENT_PROVIDERS.map(p => `payment_${p.id}`);
        let query = supabase.from('store_settings_1773100000000').select('key,value').in('key', keys);
        if (projectId) query = query.eq('project_id', projectId);
        const { data, error: fetchError } = await query;
        if (fetchError) { setEnabledProviders([]); return; }
        const settingsMap = {};
        (data || []).forEach(row => { settingsMap[row.key] = row.value; });
        const active = [];
        for (const provider of PAYMENT_PROVIDERS) {
          const setting = settingsMap[`payment_${provider.id}`];
          if (!setting) continue;
          const isExplicitlyEnabled = setting.enabled === true;
          const credentials = setting.credentials || {};
          const hasAnyCredential = Object.values(credentials).some(v => v !== null && v !== undefined && String(v).trim() !== '');
          if (isExplicitlyEnabled || hasAnyCredential) active.push(provider);
        }
        setEnabledProviders(active);
        if (active.length > 0) setSelectedPaymentMethod(active[0].id);
      } catch (e) {
        console.error('Failed to load payment providers:', e);
        setEnabledProviders([]);
      } finally {
        setLoadingProviders(false);
      }
    };
    fetchProviders();
  }, [projectId]);

  const closeModal = useCallback(() => {
    setIsCheckoutModalOpen(false);
    setTimeout(() => { 
      setOrderResult(null); setStep(1);
      setForm({ name: '', email: '', phone: '', password: '', repeatPassword: '', address: '', city: '', zipCode: '', country: 'South Africa', state: '', company: '' }); 
      setSelectedPaymentMethod('payfast'); setError(''); 
    }, 300);
  }, [setIsCheckoutModalOpen]);

  const handleNextStep = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.address || !form.city || !form.zipCode) {
      setError('Please fill in all required customer and billing fields.');
      return;
    }
    if (form.password && form.password !== form.repeatPassword) {
      setError('Passwords do not match.');
      return;
    }
    setError('');
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (cartItems.length === 0) return;
    if (enabledProviders.length > 0 && !selectedPaymentMethod) { setError('Please select a payment method to continue.'); return; }
    setIsSubmitting(true);
    setError('');
    
    try {
      if (selectedPaymentMethod === 'payfast') {
        const tempRef = `pf_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        const result = await buildPayFastUrl({ projectId, tempRef, customerName: form.name, customerEmail: form.email, cartTotal });
        if (!result?.url) { setError('Unable to initialize PayFast. Please try a different payment method.'); return; }
        savePrePaymentSession({ cartItems, customer: form, projectId, total: cartTotal, provider: 'payfast' });
        clearCart();
        window.location.href = result.url;
        return;
      }
      
      // 1. Insert Customer
      const { data: customerData } = await supabase.from('store_customers_1776316637000').insert({
        project_id: projectId,
        full_name: form.name,
        email: form.email,
        phone: form.phone,
        address: form.address,
        city: form.city,
        zip_code: form.zipCode,
        country: form.country,
        state: form.state,
        company_name: form.company
      }).select('id').single();

      // 2. Insert Order
      const { data: order, error: orderError } = await supabase.from('store_orders_1772955957776').insert({ 
        customer_id: customerData?.id,
        customer_name: form.name, 
        customer_email: form.email,
        customer_phone: form.phone,
        billing_address: form.address,
        billing_city: form.city,
        billing_zip: form.zipCode,
        billing_country: form.country,
        billing_state: form.state,
        billing_company: form.company,
        total: cartTotal, 
        status: 'paid', 
        payment_method: selectedPaymentMethod, 
        ...(projectId ? { project_id: projectId } : {}) 
      }).select('id, order_number').single();
      
      if (orderError) throw orderError;
      
      // 3. Insert Items
      const { error: itemsError } = await supabase.from('store_order_items_1772955957776').insert(
        cartItems.map(item => ({ order_id: order.id, product_id: item.id, quantity: item.quantity, price: item.price }))
      );
      if (itemsError) throw itemsError;
      
      clearCart();
      setOrderResult({ orderNumber: order.order_number, paymentMethod: selectedPaymentMethod });
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.message || 'Something went wrong during checkout.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedProvider = PAYMENT_PROVIDERS.find(p => p.id === selectedPaymentMethod);
  const noPaymentMethodsConfigured = !loadingProviders && enabledProviders.length === 0;

  if (orderResult) {
    const finalProvider = PAYMENT_PROVIDERS.find(p => p.id === orderResult.paymentMethod);
    return (
      <div className="p-12 flex flex-col items-center justify-center text-center space-y-6 h-full flex-1" style={{ backgroundColor: checkoutBg }}>
        <div className="w-24 h-24 bg-teal-50 text-teal-500 rounded-full flex items-center justify-center mb-2"><SafeIcon icon={FiCheckCircle} className="w-12 h-12" /></div>
        <h2 className="text-4xl font-black" style={{ color: titleColor }}>Order Confirmed!</h2>
        <p className="max-w-md text-base" style={{ color: textColor }}>Thank you for your purchase. We have received your order and payment. You will receive an email confirmation shortly.</p>
        {orderResult.orderNumber && (
          <div className="rounded-2xl px-8 py-5 flex items-center space-x-4 w-full max-w-sm justify-center border" style={{ backgroundColor: accent + '10', borderColor: accent + '30' }}>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: accent }}><SafeIcon icon={FiHash} className="w-6 h-6 text-white" /></div>
            <div className="text-left">
              <p className="text-[11px] font-black uppercase tracking-widest" style={{ color: textColor }}>Order Number</p>
              <p className="font-black text-2xl" style={{ color: accent }}>{orderResult.orderNumber}</p>
            </div>
          </div>
        )}
        {finalProvider && <div className="flex items-center space-x-3 text-sm px-6 py-3 rounded-xl border border-gray-100 bg-gray-50">
          {finalProvider.logoUrl ? (
            <img src={finalProvider.logoUrl} alt={finalProvider.name} className="h-7 w-auto object-contain mix-blend-multiply" />
          ) : (
            <span className="text-xl">{finalProvider.icon}</span>
          )}
          <span style={{ color: textColor }}>Paid securely via <strong>{finalProvider.name}</strong></span>
        </div>}
        <button onClick={closeModal} className="mt-6 w-full max-w-sm py-4 font-black transition-colors text-lg" style={{ backgroundColor: titleColor, color: checkoutBg, borderRadius: btnRadius }}>Continue Shopping</button>
      </div>
    );
  }

  const InputField = ({ label, keyName, type = "text", placeholder, required = false, isHalf = false }) => (
    <div className={isHalf ? "flex-1" : "w-full"}>
      <input
        type={type} required={required} value={form[keyName]}
        onChange={e => setForm({ ...form, [keyName]: e.target.value })}
        className="w-full p-3.5 outline-none transition-all font-medium text-sm border focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
        style={{ backgroundColor: inputBg, borderColor: inputBorder, borderRadius: btnRadius, color: titleColor }}
        placeholder={placeholder || label}
      />
    </div>
  );

  return (
    <div className="flex flex-col md:flex-row h-full w-full">
      {/* ── Left Side: Form / Payment ── */}
      <div className="flex-1 flex flex-col h-full overflow-hidden" style={{ backgroundColor: checkoutBg }}>
        
        {/* Header Tabs */}
        <div className="px-8 py-6 border-b flex items-center justify-between shrink-0" style={{ borderColor: inputBorder }}>
          <div className="flex items-center space-x-3 text-sm font-bold">
            <button 
              onClick={() => setStep(1)} 
              className={`transition-colors flex items-center space-x-2 ${step === 1 ? 'text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${step === 1 ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'}`}>1</div>
              <span>Customer Information</span>
            </button>
            <SafeIcon icon={FiChevronRight} className="text-gray-300 mx-1" />
            <button 
              onClick={() => { if(form.name && form.email && form.phone && form.address && form.city) setStep(2); }}
              className={`transition-colors flex items-center space-x-2 ${step === 2 ? 'text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${step === 2 ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'}`}>2</div>
              <span>Payment Details</span>
            </button>
          </div>
          <button onClick={closeModal} className="p-2 rounded-xl transition-colors hover:bg-black/5 md:hidden" style={{ color: textColor }}>
            <SafeIcon icon={FiX} className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start space-x-3 text-red-600 text-sm">
              <SafeIcon icon={FiAlertCircle} className="w-5 h-5 shrink-0 mt-0.5" /><span>{error}</span>
            </div>
          )}

          <form id="checkout-form" onSubmit={step === 1 ? handleNextStep : handleSubmit} className="space-y-8">
            
            {/* ── STEP 1: Customer & Billing ── */}
            <div style={{ display: step === 1 ? 'block' : 'none' }}>
              <div className="mb-8">
                <h3 className="text-lg font-black mb-4 flex items-center space-x-2" style={{ color: titleColor }}>
                  <SafeIcon icon={FiUser} className="w-5 h-5 text-gray-400" />
                  <span>Customer Information</span>
                </h3>
                <div className="space-y-4">
                  <InputField label="Full Name" keyName="name" required placeholder="Jane Doe" />
                  <div className="flex flex-col sm:flex-row gap-4">
                    <InputField label="Email Address" keyName="email" type="email" required placeholder="jane@example.com" isHalf />
                    <div className="flex-1">
                      <div className="relative">
                        <SafeIcon icon={FiPhone} className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 transform -translate-y-1/2" />
                        <input
                          type="tel" required value={form.phone}
                          onChange={e => setForm({ ...form, phone: e.target.value })}
                          className="w-full p-3.5 pl-10 outline-none transition-all font-medium text-sm border focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                          style={{ backgroundColor: inputBg, borderColor: inputBorder, borderRadius: btnRadius, color: titleColor }}
                          placeholder="Phone Number"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <InputField label="Set Password" keyName="password" type="password" placeholder="Set Password (Optional)" isHalf />
                    <InputField label="Repeat Password" keyName="repeatPassword" type="password" placeholder="Repeat password" isHalf />
                  </div>
                  <p className="text-xs text-blue-600 font-medium cursor-pointer hover:underline inline-block mt-1">Already have an account? Log In</p>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-black mb-4 flex items-center space-x-2" style={{ color: titleColor }}>
                  <SafeIcon icon={FiMapPin} className="w-5 h-5 text-gray-400" />
                  <span>Billing Address</span>
                </h3>
                <div className="space-y-4">
                  <InputField label="Address" keyName="address" required placeholder="123 Main Street" />
                  <div className="flex flex-col sm:flex-row gap-4">
                    <InputField label="City" keyName="city" required placeholder="City" isHalf />
                    <InputField label="Zip / Postal Code" keyName="zipCode" required placeholder="Zip / Postal Code" isHalf />
                  </div>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1">
                      <select 
                        value={form.country} onChange={e => setForm({ ...form, country: e.target.value })}
                        className="w-full p-3.5 outline-none transition-all font-medium text-sm border focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                        style={{ backgroundColor: inputBg, borderColor: inputBorder, borderRadius: btnRadius, color: titleColor }}
                      >
                        <option value="South Africa">South Africa</option>
                        <option value="United States">United States</option>
                        <option value="United Kingdom">United Kingdom</option>
                        <option value="Australia">Australia</option>
                        <option value="Namibia">Namibia</option>
                      </select>
                    </div>
                    <InputField label="State / Province" keyName="state" placeholder="State / Province" isHalf />
                  </div>
                  <InputField label="Company Name" keyName="company" placeholder="Company Name (optional)" />
                </div>
              </div>
            </div>

            {/* ── STEP 2: Payment Provider ── */}
            <div style={{ display: step === 2 ? 'block' : 'none' }}>
              <h3 className="text-lg font-black mb-4 flex items-center space-x-2" style={{ color: titleColor }}>
                <SafeIcon icon={FiCreditCard} className="w-5 h-5 text-gray-400" />
                <span>Payment Method</span>
              </h3>
              
              {loadingProviders ? (
                <div className="flex items-center space-x-3 p-5 rounded-xl border bg-gray-50 border-gray-200">
                  <div className="w-5 h-5 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin shrink-0" />
                  <span className="text-sm font-medium text-gray-600">Loading payment securely...</span>
                </div>
              ) : noPaymentMethodsConfigured ? (
                <div className="p-5 bg-amber-50 border border-amber-200 rounded-xl flex items-start space-x-3">
                  <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                  <div><p className="text-sm font-bold text-amber-800">No payment methods configured</p><p className="text-xs text-amber-700 mt-1">Please contact the store administrator.</p></div>
                </div>
              ) : (
                <div className="space-y-3">
                  {enabledProviders.map(provider => (
                    <PaymentMethodCard key={provider.id} provider={provider} isSelected={selectedPaymentMethod === provider.id} onSelect={setSelectedPaymentMethod} s={s} />
                  ))}
                </div>
              )}
            </div>

          </form>
        </div>

        {/* Footer Actions */}
        <div className="p-6 border-t shrink-0 flex items-center justify-between" style={{ backgroundColor: checkoutBg, borderColor: inputBorder }}>
          {step === 2 ? (
            <button onClick={() => setStep(1)} type="button" className="text-sm font-bold text-gray-500 hover:text-gray-800 px-4 py-2">
              Back to Details
            </button>
          ) : <div></div>}
          
          <button
            type="submit" form="checkout-form"
            disabled={isSubmitting || (step === 2 && loadingProviders) || (step === 2 && enabledProviders.length > 0 && !selectedPaymentMethod)}
            className="py-4 px-8 font-black text-base shadow-lg transition-all disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center space-x-2 hover:opacity-90 ml-auto"
            style={{ backgroundColor: accent, color: accentText, borderRadius: btnRadius }}
          >
            {isSubmitting ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <span>{step === 1 ? 'Continue to Payment' : selectedProvider ? `Pay with ${selectedProvider.name}` : 'Complete Order'}</span>
                <SafeIcon icon={step === 1 ? FiArrowRight : FiCheckCircle} className="w-5 h-5" />
              </>
            )}
          </button>
        </div>
      </div>

      {/* ── Right Side: Order Summary ── */}
      <div className="w-full md:w-[420px] shrink-0 flex flex-col border-l" style={{ backgroundColor: '#fafafa', borderColor: inputBorder }}>
        <div className="px-8 py-6 flex items-center justify-between md:hidden border-b" style={{ borderColor: inputBorder }}>
           <h3 className="text-lg font-black" style={{ color: titleColor }}>Order Summary</h3>
           <span className="text-lg font-black" style={{ color: titleColor }}>R{cartTotal.toFixed(2)}</span>
        </div>
        
        <div className="hidden md:flex px-8 py-6 items-center justify-between">
          <h3 className="text-lg font-black" style={{ color: titleColor }}>Order Summary</h3>
          <button onClick={closeModal} className="p-2 rounded-xl transition-colors hover:bg-black/5" style={{ color: textColor }}>
            <SafeIcon icon={FiX} className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-8 py-4 custom-scrollbar">
          <div className="space-y-4 mb-8">
            {cartItems.map((item, idx) => (
              <div key={`${item.id}-${idx}`} className="flex items-center space-x-4">
                <div className="relative w-16 h-16 rounded-xl border bg-white flex-shrink-0" style={{ borderColor: inputBorder }}>
                  <img src={item.image_url || 'https://via.placeholder.com/100'} alt={item.name} className="w-full h-full object-contain p-1 rounded-xl" />
                  <span className="absolute -top-2 -right-2 w-5 h-5 bg-gray-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center shadow-sm">
                    {item.quantity}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-bold truncate" style={{ color: titleColor }}>{item.name}</h4>
                  <p className="text-xs mt-1" style={{ color: textColor }}>R{Number(item.price).toFixed(2)} / month</p>
                </div>
                <div className="font-black text-sm" style={{ color: titleColor }}>
                  R{(item.price * item.quantity).toFixed(2)}
                </div>
              </div>
            ))}
          </div>

          <div className="border-t pt-6 space-y-3" style={{ borderColor: inputBorder }}>
            <div className="flex justify-between text-sm">
              <span style={{ color: textColor }}>Subtotal:</span>
              <span className="font-bold" style={{ color: titleColor }}>R{cartTotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span style={{ color: textColor }}>Tax Included:</span>
              <span style={{ color: textColor }}>VAT (15%): R{taxAmount.toFixed(2)}</span>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t" style={{ borderColor: inputBorder }}>
            <button className="text-sm text-blue-600 font-medium hover:underline">Enter promo code</button>
          </div>
        </div>

        <div className="p-8 border-t shrink-0 flex items-center justify-between" style={{ backgroundColor: '#fafafa', borderColor: inputBorder }}>
          <span className="text-base font-medium" style={{ color: textColor }}>Total:</span>
          <span className="text-3xl font-black" style={{ color: titleColor }}>R{cartTotal.toFixed(2)}</span>
        </div>
      </div>

    </div>
  );
};

const CheckoutModal = () => {
  const { isCheckoutModalOpen, storeStyles: s } = useCart();
  const modalRadius = s.checkoutRadius || '1.5rem';

  return (
    <AnimatePresence>
      {isCheckoutModalOpen && (
        <div className="fixed inset-0 z-[999999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 md:p-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 20 }}
            className="shadow-2xl w-full max-w-6xl overflow-hidden relative max-h-[95vh] h-[800px] flex flex-col bg-white"
            style={{ borderRadius: modalRadius }}
          >
            <CheckoutModalInner />
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default CheckoutModal;